import { parseTemplate } from 'url-template'
import apiGatewayClientFactory from './lib/apiGatewayCore/apiGatewayClient'
import type { SimpleHttpClientConfig } from './lib/apiGatewayCore/simpleHttpClient'
import { type AxiosResponse } from 'axios'

/*
 * Copyright 2010-2016 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */
export interface ApigClientFactory {
  newClient: (setting: ApigClientSetting) => ApiGatewayClient
}

// @ts-expect-error (newClientはすぐ後で定義している...元ソースのコードを大きく変更したくないので、こうしている)
const apigClientFactory: ApigClientFactory = {}

export interface KeyValues {
  [key: string]: string
}

export interface ApiGatewayClient {
  invokeApi: (
    params: KeyValues,
    pathTemplate: string,
    method: string,
    additionalParams: AdditionalParams,
    body: string
  ) => Promise<AxiosResponse>
}

export interface ApiGatewayRequest {
  verb: string
  path: string
  headers: KeyValues
  timeout: undefined | number
  queryParams: undefined | KeyValues
  body: undefined | null | string
}

export interface AdditionalParams {
  headers?: KeyValues | undefined
  timeout?: undefined | number
  queryParams?: KeyValues | undefined
}

export interface ApigClientSetting {
  invokeUrl?: string
  accessKey?: string
  secretKey?: string
  sessionToken?: string
  region?: string
}

interface ApigClientConfig {
  accessKey: string
  secretKey: string
  sessionToken: string
  region: string
  invokeUrl: string
  service: string
  defaultContentType: string
  defaultAcceptType: string
  systemClockOffset: number
  headers: KeyValues
  host: string
  retries?: number
  retryCondition?: string
  retryDelay?: number
  apiKey?: string
}

apigClientFactory.newClient = function (setting: ApigClientSetting) {
  // @ts-expect-error invokeApiは後で登録する（元コードから大きく変えたくないので、あえて）
  const apigClient: ApiGatewayClient = {}

  const config: ApigClientConfig = Object.assign({
    accessKey: setting.accessKey,
    secretKey: setting.secretKey,
    sessionToken: setting.sessionToken,
    region: setting.region,
    invokeUrl: setting.invokeUrl,
    service: 'execute-api',
    defaultContentType: 'application/json',
    defaultAcceptType: 'application/json',
    systemClockOffset: 0,
    headers: {},
    host: undefined
  }) // extract endpoint and path from url

  const invokeUrl = config.invokeUrl

  if (!invokeUrl) {
    throw new Error('invokeUrl must be specified!')
  }

  // @ts-expect-error pattern matching must be not null
  const endpoint = /(^https?:\/\/[^/]+)/g.exec(invokeUrl)[1]
  const pathComponent = invokeUrl.substring(endpoint.length)
  const sigV4ClientConfig = {
    accessKey: config.accessKey,
    secretKey: config.secretKey,
    sessionToken: config.sessionToken,
    serviceName: config.service,
    region: config.region,
    endpoint: endpoint,
    defaultContentType: config.defaultContentType,
    defaultAcceptType: config.defaultAcceptType,
    systemClockOffset: config.systemClockOffset,
    retries: config.retries,
    retryCondition: config.retryCondition,
    retryDelay: config.retryDelay,
    host: config.host
  }
  let authType = 'NONE'

  if (
    sigV4ClientConfig.accessKey !== undefined &&
    sigV4ClientConfig.accessKey !== '' &&
    sigV4ClientConfig.secretKey !== undefined &&
    sigV4ClientConfig.secretKey !== ''
  ) {
    authType = 'AWS_IAM'
  }

  const simpleHttpClientConfig: SimpleHttpClientConfig = {
    accessKey: undefined,
    secretKey: undefined,
    sessionToken: undefined,
    serviceName: undefined,
    region: undefined,
    host: undefined,
    systemClockOffset: undefined,
    endpoint: endpoint,
    defaultContentType: config.defaultContentType,
    defaultAcceptType: config.defaultAcceptType,
    retries: config.retries,
    retryCondition: config.retryCondition,
    retryDelay: config.retryDelay,
    headers: config.headers
  }

  const apiGatewayClient = apiGatewayClientFactory.newClient!(
    simpleHttpClientConfig,
    sigV4ClientConfig
  )

  apigClient.invokeApi = function (
    params: KeyValues,
    pathTemplate: string,
    method: string,
    additionalParams: AdditionalParams,
    body: string
  ) {
    if (additionalParams === undefined) additionalParams = {}
    if (body === undefined) body = ''
    const request: ApiGatewayRequest = {
      verb: method.toUpperCase(),
      path: pathComponent + parseTemplate(pathTemplate).expand(params),
      headers: additionalParams.headers || {},
      timeout: additionalParams.timeout || 0,
      queryParams: additionalParams.queryParams,
      body: body
    }
    const apiKey = typeof config.apiKey === 'string' ? config.apiKey : undefined
    return apiGatewayClient.makeRequest(request, authType, additionalParams, apiKey)
  }

  return apigClient
}

export default apigClientFactory
